import React, { useState, useEffect, useMemo } from "react";
import { debounce } from "debounce";
import { Autocomplete, TextField, Paper, Checkbox, Stack, Typography, Chip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import Flag from "react-country-flag";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { getCitiesApi } from "../../../services/api/common";

const useStyles = makeStyles(() => ({
    icon: {
        width: 22,
        height: 16,
    },
}));

export default function CityMultiselect({ iso2Countries, defaultValues = [], onSelected, displayTags = true }) {
    const [cities, setCities] = useState([]);
    const [selectedCities, setSelectedCities] = useState([...defaultValues]);
    const classes = useStyles();

    useEffect(() => {
        setSelectedCities([...defaultValues]);
    }, [defaultValues]);

    const getCities = useMemo(
        () =>
            debounce(async (cityName) => {
                const { data } = await getCitiesApi({
                    q: { name: cityName },
                    sort_by: { name: "asc" },
                    country: iso2Countries,
                });
                setCities(data.records);
            }, 300),
        [iso2Countries, setCities],
    );

    const handleOnInputChange = (e) => {
        if (!e) return;

        const cityName = e.target.value;
        if (!cityName) {
            setCities([]);
            return;
        }

        getCities(cityName);
    };

    const handleOnChange = (e, selected, reason) => {
        if (!displayTags && reason === "removeOption") return;

        if (reason === "clear" || (reason === "removeOption" && !selected.length)) {
            setSelectedCities([]);
            onSelected([]);
            return;
        }
        if (!selected) return;

        const lastSelected = selected[selected.length - 1];
        if (!lastSelected.name) return;

        setSelectedCities(selected);
        if (onSelected) {
            onSelected(selected);
        }
    };

    return (
        <Autocomplete
            disabled={iso2Countries.length < 1}
            disablePortal
            multiple
            freeSolo
            disableClearable={!displayTags}
            popupIcon={false}
            value={selectedCities}
            options={cities.filter((element) => selectedCities.findIndex((item) => item.name === element.name) < 0)}
            getOptionLabel={(option) => option.name}
            fullWidth
            variant="outlined"
            size="small"
            onInputChange={handleOnInputChange}
            onChange={handleOnChange}
            PaperComponent={({ children }) => <Paper variant="autocompleteDropdown">{children}</Paper>}
            renderInput={(params) => (
                <TextField color="secondary" {...params} label="City" placeholder="Select city..." margin="dense" />
            )}
            renderOption={(props, option, { selected }) => (
                <li {...props}>
                    <Checkbox icon={<CheckBoxOutlineBlankIcon />} checkedIcon={<CheckBoxIcon />} checked={selected} />
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Flag countryCode={option.country.iso2} svg className={classes.icon} />
                        <Typography>{option.name}</Typography>
                    </Stack>
                </li>
            )}
            renderTags={
                displayTags
                    ? (items, getTagProps) =>
                          items.map((item, index) => (
                              <Chip
                                  label={item.name}
                                  {...getTagProps({ index })}
                                  icon={<Flag countryCode={item.country.iso2} svg className={classes.icon} />}
                              />
                          ))
                    : () => null
            }
        />
    );
}
